import { Inject, Injectable } from "@angular/core";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { BehaviorSubject, map, Observable, Subject, forkJoin } from "rxjs";
import { environment } from "../../environments/environment";
import { config } from "../../environments/config";
import { HttpService } from "./http.service";
import { Router } from "@angular/router";
import {
  UpdateShelfProductsPayload,
  BulkUpdateShelfProductsPayload,
  ProductSyncData,
} from "../main-container/planogram/v2/plano.types";
import { CommonModalComponent } from "../shared/common-modal/common-modal.component";
import { SEGMENT_STYLE_COLORS_ARRAY } from "../main-container/planogram/v2/plano.constants";
import * as XLSX from "xlsx";
import { LocalStorageService } from "./local-storage.service";
import { CatExpertRouterLink } from "../app-routing-enum";

const BASE_URL = environment.baseUrl;
const BASE_PLANO_URL = environment.basePlanoUrl;
const BASE_PRODUCT_URL = environment.baseProductUrl;
const EXP_BASE_URL = environment.baseUrlV2;
const EXP_BASE_URL_V3 = environment.baseUrlV3;
const PLANO_WEBSOCKET_URL = environment.planogramWebsocketUrl;
const PLANO_FIXTURE_WEBSOCKET_URL = environment.fixtureWebsocketUrl;
const URL = environment.url;
const EDAURL = environment.edaurl;
const EDAURLV2 = environment.edaurlV2;
const EXPURL = environment.expurl;
const SIMULATIONURL = environment.simulationurl;
const USERURL = environment.userUrl;
const SOCKET_URL = environment.socketBaseUrl + environment.socketCommonPath;
const ACCESS_TOKEN_EXPIRY_DURATION = 5;
const USERS_V3_URL = environment.userUrlV3;
const API_URL = environment.apiUrl;
const GO_PROCESS_URL = environment.goProcess;
const PHOBASEURL = environment.phoBaseUrl;
const GOPROCESSSTATES = environment.goProcessStates;
const ASSORTMENT_URL = environment.assortmentUrl;
const GOPROCESSDOWNLOAD = environment.goProcessDownload;
const AD_CLUSTERING_URL = environment.apiUrl + "api/v1/";

const websocketConfig = {
  planogram: {
    webSocketUrl: PLANO_WEBSOCKET_URL,
    webSocketUrlFixture: PLANO_FIXTURE_WEBSOCKET_URL,
  },
};

const apiData = {
  user: {
    me: `${USERURL}me`,
    meV3: `${USERS_V3_URL}users/me`,
    login: `${USERURL}login`,
    users: `${USERURL}users/`,
    acceptTerms: `${USERURL}/user/termscondition`,
    usersByID: `${EXP_BASE_URL}/users/`,
    getUser: `${USERURL}user/search`,
    searchUserV3: `${USERS_V3_URL}users/search`,
    logout: `${USERURL}logout`,
    refreshToken: `${USERURL}refresh_access_token`,
    newsletter: `${USERURL}newsletter`,
    approval: `${USERURL}user/approval`,
    approvalV3: `${USERS_V3_URL}users/onboard`,
    sharedWith: `${USERURL}bulk/users/detail`,
    countriesList: `${USERS_V3_URL}countries`,
    countriesListWithZone: `${USERS_V3_URL}countries/zone/list`,
  },
  roles: {
    permission_mappings: `${USERS_V3_URL}roles/permissions/mapping`,
  },
  projects: {
    projects: `${BASE_URL}projects`,
    eda: `${EDAURL}eda`,
    edaV2: `${EDAURLV2}`,
    keyaccounts: `${URL}key_account_by_country`,
    regions: `${URL}regions_by_key_account_and_country`,
    createkeyaccounts: `${URL}key_accounts`,
    createregions: `${URL}regions`,
    zone: `${URL}zones`,
    users: `${USERURL}users`,
    requests: `${USERURL}users/approvals`,
    approval: `${USERURL}user/approvals`,
    bulkApproval: `${USERURL}users/approvals/bulk`,
    status: `${USERURL}user/status`,
  },
  admin: {
    zones: `${USERS_V3_URL}zones`,
    keyAccounts: `${USERS_V3_URL}key-accounts`,
    countries: `${USERS_V3_URL}countries`,
    userRequests: `${USERS_V3_URL}users/requests`,
    users: `${USERS_V3_URL}admin/users`,
    onboardUser: `${USERS_V3_URL}users/onboard`,
    updateZone: (zoneId: string) => `${USERS_V3_URL}zones/${zoneId}`,
    deleteZone: (zoneId: string) => `${USERS_V3_URL}zones/${zoneId}`,
    updateKeyAccount: (keyAccountId: string) =>
      `${USERS_V3_URL}key-accounts/${keyAccountId}`,
    getUsers: (zoneId: string) => `${USERS_V3_URL}zones/${zoneId}/users`,
    getStatus: (userId: string) => `${USERS_V3_URL}users/${userId}/status`,
    updateUser: (userId: string) => `${USERS_V3_URL}users/${userId}`,
    getRequests: (zoneId: string) => `${USERS_V3_URL}zones/${zoneId}/requests`,
    getExperiments: (zoneId: string) =>
      `${EXP_BASE_URL_V3}/zones/${zoneId}/experiments`,
    getKeyAccounts: (zoneId: string) =>
      `${USERS_V3_URL}zones/${zoneId}/key-accounts`,
    migrateZone: (zoneId: string, userId: string) =>
      `${USERS_V3_URL}zones/${zoneId}/users/${userId}/migrate`,
    revokeZone: (zoneId: string, userId: string) =>
      `${USERS_V3_URL}zones/${zoneId}/users/${userId}/revoke`,
    approveRejectRequest: (zoneId: string, requestId: string) =>
      `${USERS_V3_URL}zones/${zoneId}/requests/${requestId}`,
    syncKeyAccounts: (zoneId: string, userId: string) =>
      `${USERS_V3_URL}zones/${zoneId}/users/${userId}/key-accounts`,
    requestNewZone: (zoneId: string) => `${USERS_V3_URL}users/zones/${zoneId}`,
    requestNewKeyAccount: (zoneId: string) =>
      `${USERS_V3_URL}users/zones/${zoneId}/key-accounts`,
    getTicketRequests: (status: string) =>
      `${USERS_V3_URL}tickets?status=${status}`,
    downloadTicketRequests: (status: string) =>
      `${USERS_V3_URL}tickets/export/excel?status=${status}`,
    changeTicketStatus: (ticketId: string, status: string) =>
      `${USERS_V3_URL}tickets/${ticketId}/status/${status}`,
  },
  planogram: {
    getValidationRules: () => `${BASE_PRODUCT_URL}api/v3/rules-master`,
    getDropdownValuesForProductValidations: (key_account_id: string) =>
      `${BASE_PRODUCT_URL}api/v3/bulk-upload/get-dropdown-values?key_account_id=${key_account_id}`,
    runMlValidations: () => `${BASE_PRODUCT_URL}api/v2/attribute-predictions`,
    fileUpload: `${BASE_PLANO_URL}api/v1/`,
    fileUploadV2: (version: string = "v2") =>
      `${BASE_PLANO_URL}api/${version}/`,
    psaFileUpload: `${BASE_PLANO_URL}api/v2/uploads/experiment`,
    plnFileUpload: `${BASE_PLANO_URL}api/v3/uploads/experiment`,
    downloadSampleTemplate: `${BASE_PLANO_URL}api/v1/planogram/sample/download`,
    GetPSAOfPlanogram: (
      expId: string,
      planId: string,
      version: string = "v1"
    ) => `${BASE_PLANO_URL}api/${version}/${expId}/plans/${planId}/export/psa`,
    GetPLNOfPlanogram: (
      expId: string,
      planId: string,
      version: string = "v2"
    ) => `${BASE_PLANO_URL}api/${version}/${expId}/plans/${planId}/export/pln`,
    GetExcelOfPlanogram: (
      expId: string,
      planId: string,
      version: string = "v1"
    ) =>
      `${BASE_PLANO_URL}api/${version}/${expId}/plans/${planId}/export/excel`,
    getExcelOfAllPlanograms: (expId: string, version: string = "v1") =>
      `${BASE_PLANO_URL}api/${version}/${expId}/export/excel`,
    GetLeftoutExcelOfPlanogram: (expId: string) =>
      `${BASE_PLANO_URL}api/v1/${expId}/leftout/export/excel`,
    GetPDFOfPlanogram: (expId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v1/${expId}/plans/${planId}/export/pdf`,
    getCustomPDF: () => `${API_URL}utils/api/v2/generate/pdf`,
    saveShelf: `${BASE_PLANO_URL}api/v1/`,
    getExistingPlanoDetails: `${BASE_PLANO_URL}/api/v1/`,
    getAllocatedPlanoDetails: `${BASE_PLANO_URL}api/v1/planogram/segment_allocation`,
    getPlansOfPlanogram: (expId: string, version: string = "v1") =>
      `${BASE_PLANO_URL}api/${version}/${expId}/plans`,
    // getPlansOfPlanogram: (expId: string) =>
    //   `${BASE_PLANO_URL}api/v3/${expId}/plans`,
    getFiltersOfPlanogram: (expId: string) =>
      `${BASE_PLANO_URL}api/v1/${expId}/filters`,
    getProductsOnFilters: (expId: string) =>
      `${BASE_PLANO_URL}api/v1/${expId}/products/list`,
    getPlanOfPlano: (expId: string, planId: string, version: string = "v1") =>
      `${BASE_PLANO_URL}api/${version}/${expId}/plans/${planId}`,
    getSystemPlanOfPlano: (expId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v1/${expId}/plans/${planId}/system`,
    updatePlanOfShelfDimension: (expId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v1/${expId}/plans/${planId}/dimensions`,
    updatePlanOfSegmentAllocation: (expId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v1/${expId}/plans/${planId}/segment-allocations`,
    updatePlanGrid: (expId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v1/${expId}/plans/${planId}`,
    saveTemplate: () => `${BASE_PLANO_URL}api/v1/templates`,
    saveSku: (expId: string, version: string = "v3") =>
      `${BASE_PLANO_URL}api/${version}/${expId}/products`,
    deleteSku: (expId: string) => `${BASE_PLANO_URL}api/v3/${expId}/products`,
    syncSkuPlan: (expId: string) =>
      `${BASE_PLANO_URL}api/v3/${expId}/products/sync`,
    getItemsForAPlan: (expId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v1/${expId}/plans/${planId}/products`,
    syncPlanProducts: (expId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v2/${expId}/plans/${planId}/products`,
    deleteAllProducts: (expId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v2/${expId}/plans/${planId}/products/all`,
    processCheck: (taskId: string) => `${GO_PROCESS_URL}${taskId}/status`,
    getLeftout: (expId: string) =>
      `${BASE_PLANO_URL}api/v1/${expId}/products/leftout`,
    updateCellItems: (expId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v1/${expId}/plans/${planId}/cells/update`,
    bulkUpdateCellItems: (expId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v2/${expId}/plans/${planId}/cells/update`,
    resetPlano: (expId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v1/${expId}/plans/${planId}/reset`,
    planoFromLeftout: (expId: string) =>
      `${BASE_PLANO_URL}api/v1/${expId}/products/leftout/refresh`,
    getSystemGenItemsForAPlan: (expId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v1/${expId}/plans/${planId}/system/products`,
    getOverallManufacturerShare: (expId: string) =>
      `${BASE_PLANO_URL}api/v1/${expId}/space-shares`,
    getImportTaskId: (
      expId: string,
      version: string = "v2",
      includePSA: string = ""
    ) =>
      `${BASE_PLANO_URL}api/${version}/${expId}/import${includePSA ? "/" + includePSA : ""}`,
    deletePlanFromPlanogram: (expId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v1/${expId}/plans/${planId}`,
    createPlanoInitValidations: (expId: string, version: string = "v2") =>
      `${BASE_PLANO_URL}api/${version}/${expId}/validations`,
    getPlansOfPlanogramV2: (expId: string, version: string = "v2") =>
      `${BASE_PLANO_URL}api/${version}/${expId}/plans`,
    getAllPSAOfPlanogram: (expId: string, version: string = "v1") =>
      `${BASE_PLANO_URL}api/${version}/${expId}/export/psa`,
    getAllPLNOfPlanogram: (expId: string, version: string = "v2") =>
      `${BASE_PLANO_URL}api/${version}/${expId}/export/pln`,
    getAllPlansProducts: (expId: string) =>
      `${BASE_PLANO_URL}api/v1/${expId}/products/all`,
    uploadProducts: (expId: string) =>
      `${BASE_PLANO_URL}api/v1/${expId}/products/upload`,
    reAllocateProducts: (
      expId: string,
      planId: string,
      version: string = "v1"
    ) =>
      `${BASE_PLANO_URL}api/${version}/${expId}/plans/${planId}/rules/new/reallocate`,
    getConstraintControllerTaskId: (
      expId: string,
      planId: string,
      version: string = "v1"
    ) =>
      `${BASE_PLANO_URL}api/${version}/${expId}/plans/${planId}/rules/new/constraint_controller`,
    getSaturationDemandCutoffTaskId: (
      expId: string,
      planId: string,
      version: string = "v1"
    ) =>
      `${BASE_PLANO_URL}api/${version}/${expId}/plans/${planId}/rules/new/saturation_demand_cutoff`,
    versionHistory: (expId: string, planId: string, version: string = "v1") =>
      `${BASE_PLANO_URL}api/${version}/${expId}/plans/${planId}/versions`,
    setVersionAsCurrent: (
      expId: string,
      planId: string,
      versionId: string,
      version: string = "v1"
    ) =>
      `${BASE_PLANO_URL}api/${version}/${expId}/plans/${planId}/versions/${versionId}/current`,
    spacePlanoInitValidation: (refId: string) =>
      `${BASE_PLANO_URL}api/v3/${refId}/validations`,
    // createSpacePlano: (refId: string) =>
    //   `${BASE_PLANO_URL}api/v2/${refId}/plans`,
    // createSpacePlanoDynamicNesting: (refId: string) =>
    //   `${BASE_PLANO_URL}api/v2/${refId}/plans/dynamic-nesting`,
    cloneSpacePlanoDynamicNesting: (refId: string, planCloneId: string) =>
      `${BASE_PLANO_URL}api/v2/${refId}/plans/${planCloneId}/dynamic-nesting/clone`,
    getSpacePlanData: (refId: string, planId: string, version: string = "v2") =>
      `${BASE_PLANO_URL}api/${version}/${refId}/plan/${planId}`,
    dynamicNestingPreprocessing: (
      refId: string,
      planId: string,
      version: string = "v2"
    ) =>
      `${BASE_PLANO_URL}api/${version}/${refId}/plans/${planId}/dynamic-nesting/preprocessing`,
    dynamicNesting: (refId: string, planId: string, version: string = "v2") =>
      `${BASE_PLANO_URL}api/${version}/${refId}/plans/${planId}/dynamic-nesting`,
    // `${BASE_PLANO_URL}api/v3/${refId}/plans/${planCloneId}/dynamic-nesting/clone`,
    // getSpacePlanData: (refId: string, planId: string) =>
    //   `${BASE_PLANO_URL}api/v3/${refId}/plan/${planId}`,
    // dynamicNestingPreprocessing: (refId: string, planId: string) =>
    //   `${BASE_PLANO_URL}api/v3/${refId}/plans/${planId}/dynamic-nesting/preprocessing`,
    // dynamicNesting: (refId: string, planId: string) =>
    //   `${BASE_PLANO_URL}api/v3/${refId}/plans/${planId}/dynamic-nesting`,
    updateSpacePlanData: (refId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v3/${refId}/plans/${planId}/dynamic-nesting`,
    initSpaceDynamicNestingValidations: (refId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v3/${refId}/plans/${planId}/dynamic-nesting/validations`,
    spaceImpactAnalysis: (refId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v3/${refId}/plans/${planId}/impact_analysis`,
    dynamicNestingRefresh: (refId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v3/${refId}/plans/${planId}/dynamic-nesting/refresh`,
    imageVersionUpdate: (expId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v1/${expId}/plans/${planId}/image-version`,
    fetchColumnMappingData: (expId: string) =>
      `${BASE_PLANO_URL}api/v2/${expId}/psa/data/get`,
    mirroringPlanogram: (expId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v2/${expId}/plans/${planId}`,
    migratePlanogram: (expId: string) =>
      `${BASE_PLANO_URL}api/v3/${expId}/migrate-to-v3`,
    packVariant: (expId: string, productId: string) =>
      `${BASE_PLANO_URL}api/v2/${expId}/products/${productId}`,
    packVariantImageUpload: (expId: string, variant: string) =>
      `${BASE_PLANO_URL}api/v2/${expId}/products/${variant}/variants-images/upload`,
    packVariantImageDelete: (expId: string, variant: string) =>
      `${BASE_PLANO_URL}api/v2/${expId}/products/${variant}/variants-images/delete`,

    //Plano Dashboard APIs
    getCharts: (expId: string) => `${BASE_PLANO_URL}api/v2/${expId}/charts`,
    updateChart: (expId: string, chartId: string = "") =>
      `${BASE_PLANO_URL}api/v2/${expId}/charts${chartId ? "/" + chartId : ""}`,
    deleteChart: (expId: string, chartId: string) =>
      `${BASE_PLANO_URL}api/v2/${expId}/charts/${chartId}`,
    fetchChartData: (expId: string) =>
      `${BASE_PLANO_URL}api/v2/${expId}/charts/aggregations/data`,

    //Space Plano Create Flow
    downloadMappingFile: (
      expId: string,
      planId: string,
      version: string = "v2"
    ) =>
      `${BASE_PLANO_URL}api/${version}/${expId}/plans/${planId}/mapping-file/download`,
    uploadMappingFile: (
      expId: string,
      planId: string,
      version: string = "v2"
    ) =>
      `${BASE_PLANO_URL}api/${version}/${expId}/plans/${planId}/mapping-validation`,
    getPreviousExperiments: () => `${EXP_BASE_URL_V3}/experiments`,
    getStores: (version: string = "v2") =>
      `${BASE_PLANO_URL}api/${version}/templates/seasonality`,
    createSpacePlano: (expId: string, version: string = "v2") =>
      `${BASE_PLANO_URL}api/${version}/${expId}/plans/space-assortment`,
    createSpacePlanoDynamicNesting: (
      expId: string,
      planId: string,
      version: string = "v2"
    ) =>
      `${BASE_PLANO_URL}api/${version}/${expId}/plans/${planId}/dynamic-nesting`,
    // downloadMappingFile: (expId: string, planId: string) =>
    //   `${BASE_PLANO_URL}api/v3/${expId}/plans/${planId}/mapping-file/download`,
    // uploadMappingFile: (expId: string, planId: string) =>
    //   `${BASE_PLANO_URL}api/v3/${expId}/plans/${planId}/mapping-validation`,
    // getPreviousExperiments: () => `${EXP_BASE_URL_V3}/experiments`,
    // getStores: () => `${BASE_PLANO_URL}api/v3/templates/seasonality`,
    // createSpacePlano: (expId: string) =>
    //   `${BASE_PLANO_URL}api/v3/${expId}/plans/space-assortment`,
    // createSpacePlanoDynamicNesting: (expId: string, planId: string) =>
    //   `${BASE_PLANO_URL}api/v3/${expId}/plans/${planId}/dynamic-nesting`,
    publishSpaceExperiment: (
      expId: string,
      planId: string,
      statusType: string
    ) =>
      `${BASE_PLANO_URL}api/v3/${expId}/plans/${planId}/status/${statusType}`,
    copyAssortmentExpData: (expId: string, refId: string) =>
      `${API_URL}projects/api/v3/experiments/${expId}/copy-to/${refId}`,
    getKPISkus: (refId: string) => `${BASE_PLANO_URL}api/v3/${refId}/kpi/sku`,

    // Planogram V3 API's
    getPlanogramObjects: (refId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v3/${refId}/plans/${planId}/objects`,
    getPlanogramTask: (refId: String, planId: string) =>
      `${BASE_PLANO_URL}api/v3/${refId}/plans/${planId}/tasks/trigger`,
    getPlansList: (expId: string) => `${BASE_PLANO_URL}api/v3/${expId}/plans`,
    getSystemRecommended: (expId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v3/${expId}/plans/${planId}/system`,
    getSystemRecommendedObjects: (expId: string, planId: string) =>
      `${BASE_PLANO_URL}api/v3/${expId}/plans/${planId}/system/objects`,

    //Product library api's
    getProductLibraryList: () => `${BASE_PRODUCT_URL}api/v1/all/products/list`,

    getPlanogramList: `${BASE_PLANO_URL}api/v3/plans/list`,

    initPlanogram: (expId: String) =>
      `${BASE_PLANO_URL}api/v3/${expId}/init/planogram`,
    //Customize PDF Template APIs
    getAllTemplates: () => `${API_URL}utils/api/v2/pdf/template`,
    deleteTemplate: (templateId: string) =>
      `${API_URL}utils/api/v2/template/${templateId}`,
    listTemplates: () => `${API_URL}utils/api/v2/templates`,
    createTemplate: () => `${API_URL}utils/api/v2/templates`,
    updateTemplate: (templateId: string) =>
      `${API_URL}utils/api/v2/templates/${templateId}`,
    getTemplate: (templateId: string) =>
      `${API_URL}utils/api/v2/templates/${templateId}`,
    exportPDF: () => `${API_URL}utils/api/v2/templates/pdfreport`,

    // Bulk import api's
    bulkImportUpload: () => `${BASE_PLANO_URL}api/v3/bulk-imports`,
    bulkImportPolling: (bulkOperationId: string) =>
      `${BASE_PLANO_URL}api/v3/bulk-operations/${bulkOperationId}`,
    getBulkOperationsList: (limit: number, offset: number) =>
      `${BASE_PLANO_URL}api/v3/bulk-operations?limit=${limit}&offset=${offset}`,
    bulkImportMapping: (bulkOperationId: string) =>
      `${BASE_PLANO_URL}api/v3/bulk-operations/${bulkOperationId}/start-import`,
    processResultCheck: (taskId: string) => `${GO_PROCESS_URL}${taskId}`,

    // Space Block template APIs
    spaceBlockTemplates: () => `${BASE_PLANO_URL}api/v3/space-block-templates`,
    getSpaceBlockTemplate: (templateId: string) =>
      `${BASE_PLANO_URL}api/v3/space-block-templates/${templateId}`,
    updateSpaceBlockTemplate: (templateId: string) =>
      `${BASE_PLANO_URL}api/v3/space-block-templates/${templateId}`,
    deleteSpaceBlockTemplate: (templateId: string) =>
      `${BASE_PLANO_URL}api/v3/space-block-templates/${templateId}`,

    pinWidget: (expId: string) =>
      `${BASE_PLANO_URL}api/v3/${expId}/planogram-widgets`,
    planStatus: (expId: string, planId: string, status: string) =>
      `${BASE_PLANO_URL}api/v3/${expId}/plans/${planId}/status/${status}`,
    getFolderList: () => `${EXP_BASE_URL_V3}/folders/root/tree`,
    getPlansKPI: () => `${BASE_PLANO_URL}api/v3/plans/kpi`,
    parallelProcessCheck: (taskId: string) =>
      `${GOPROCESSDOWNLOAD}parallel-tasks/${taskId}/status`,
    getFolderExperiments: (folderId: string = "root") =>
      `${EXP_BASE_URL_V3}/folders/${folderId}/experiments`,
    getCountryWiseKPI: () => `${BASE_PLANO_URL}api/v3/plans/kpi/countries`,

    // Product Library APIs
    getProductsListV2: (version: string = "v2") =>
      `${BASE_PRODUCT_URL}api/${version}/products/list`,
    getVariantsList: () => `${BASE_PRODUCT_URL}api/v3/products/variants/list`,
    saveAttribute: () => `${BASE_PRODUCT_URL}api/v2/products/attribute`,
    updateAttribute: (id: string) =>
      `${BASE_PRODUCT_URL}api/v2/products/attribute/${id}`,
    addSku: (version: string = "v2") =>
      `${BASE_PRODUCT_URL}api/${version}/products`,
    updateSku: (version: string = "v2", productId: string) =>
      `${BASE_PRODUCT_URL}api/${version}/products/${productId}`,
    deleteSkuFromLibrary(version: string = "v2", productId: string) {
      return `${BASE_PRODUCT_URL}api/${version}/products/${productId}`;
    },
    attributesList: (keyAccountId: string) =>
      `${BASE_PRODUCT_URL}api/v2/products/attribute/${keyAccountId}`,
    bulkUploadSku: (keyAccountId: string) =>
      `${BASE_PRODUCT_URL}api/v3/products/bulk-uploads/validation`,
    bulkUploadProductsUpdate: (keyAccountId: string) =>
      `${BASE_PRODUCT_URL}api/v3/products/bulk-uploads/record-update`,
    getBulkUploadSku: (taskId: string) =>
      `${BASE_PRODUCT_URL}api/v2/bulk-upload/products/result/${taskId}`,
    bulkUploadSkuImage: (version: string = "v2") =>
      `${BASE_PRODUCT_URL}api/${version}/products/bulk-uploads/images`,
    getBulkUploadSkuImageStatus: (taskId: string) =>
      `${BASE_PRODUCT_URL}api/v2/bulk-upload/images/result/${taskId}`,
    getVersionHistory: (version: string = "v2", productId: string) =>
      `${BASE_PRODUCT_URL}api/${version}/products/${productId}/versions`,
    restoreVersion: (version: string = "v2", versionHistoryId: string) =>
      `${BASE_PRODUCT_URL}api/${version}/products/versions/${versionHistoryId}/restore`,
    syncSku: (version: string = "v2") =>
      `${BASE_PRODUCT_URL}api/${version}/products/sync`,

    syncSkus: () => `${BASE_PRODUCT_URL}api/v3/products/bulk-uploads/save`,

    getLibrariesDetails: (version: string = "v3") =>
      `${BASE_PRODUCT_URL}api/${version}/products/bulk-uploads/bulk-upload-status`,
    getLibrariesDetailById: (version: string = "v3", libraryId: string) =>
      `${BASE_PRODUCT_URL}api/${version}/products/bulk-uploads/bulk-upload-records/${libraryId}`,

    // Bulk export PDF
    bulkExportPDF: () => `${BASE_PLANO_URL}api/v3/bulk-exports`,
    bulkOperationsPolling: (bulkOperationId: string) =>
      `${BASE_PLANO_URL}api/v3/bulk-operations/${bulkOperationId}`,
    bulkEdits: () => `${BASE_PLANO_URL}api/v3/bulk-edits`,

    getDashboardList: () => `${EXP_BASE_URL_V3}/dashboards/list`,
    createDashboard: () => `${EXP_BASE_URL_V3}/dashboards`,
    updateDashboard: (dashboardId: string) =>
      `${EXP_BASE_URL_V3}/dashboards/${dashboardId}`,
    getDashboardDetails: (dashboardId: string) =>
      `${EXP_BASE_URL_V3}/dashboards/${dashboardId}`,
    getExperimentDetails: () => `${API_URL}projects/api/v3/experiments/bulk`,
    shareDashboardDetails: (dashboardId: string) =>
      `${EXP_BASE_URL_V3}/dashboards/${dashboardId}/share`,

    // Ai Imagery Generation APIs
    getChatSessions: () => `${BASE_PRODUCT_URL}api/v2/chat-sessions`,
    getChatSessionsById: (chatId: string) =>
      `${BASE_PRODUCT_URL}api/v2/chat-sessions/${chatId}`,
    promptsFromSessions: (sessionId: string) =>
      `${BASE_PRODUCT_URL}api/v2/chat-sessions/${sessionId}/prompts`,
    uploadLibraryImages: () => `${BASE_PRODUCT_URL}api/v2/library-images`,
    libraryImages: () => `${BASE_PRODUCT_URL}api/v2/library-images`,
    updateLibraryImage: (imageId: string) =>
      `${BASE_PRODUCT_URL}api/v2/library-images/${imageId}`,
    bulkUploadPrompts: () => `${BASE_PRODUCT_URL}api/v2/bulk-chat-sessions`,
    getBulkChatSessions: (chatSessionId: string) =>
      `${BASE_PRODUCT_URL}api/v2/bulk-chat-sessions/${chatSessionId}`,
    downloadProductTemplate: (version: string = "v2") =>
      `${BASE_PRODUCT_URL}api/${version}/products/bulk-uploads/template`,
    downloadTemplate: (keyAccountId: string, keyAccountName: string) =>
      `${BASE_PRODUCT_URL}api/v3/products/bulk-uploads/template?key_account_id=${keyAccountId}&key_account=${keyAccountName}`,
    aiImageryTemplates: () => `${BASE_PRODUCT_URL}api/v2/prompt-templates`,
    aiImageryUpdateTemplate: (templateId: string) =>
      `${BASE_PRODUCT_URL}api/v2/prompt-templates/${templateId}`,
  },
  templates: {
    getTemplateDetails: (templateId: string) =>
      `${BASE_PLANO_URL}api/v1/templates/${templateId}`,
    getTemplates: (options: {
      country: string;
      key_account: string;
      region: string;
    }) =>
      `${BASE_PLANO_URL}api/v1/templates?country=${options.country}&key_account=${options.key_account}`,
  },
  experiments: {
    getExperiment: `${EXP_BASE_URL_V3}`,
    getExperimentV1: `${EXP_BASE_URL}`,
    getExperiments: `${EXP_BASE_URL}/experiments`,
    getExperimentsV3: `${EXP_BASE_URL_V3}/experiments`,
    dbconnectV1: `${BASE_URL}connect_db`,
    dbStatusV1: `${BASE_URL}db/status`,
    dbconnectV2: `${EXP_BASE_URL}/connect_db`,
    dbStatusV2: `${EXP_BASE_URL}/db/status`,
    createExperiment: `${BASE_URL}experiments`,
    uploadFile: `${BASE_URL}upload_file_experiment`,
    uploadFileV2: `${BASE_URL}/experiments/:experiment_id/file/:category`,
    columnMapping: `${BASE_URL}column_mapping`,
    validation: `${BASE_URL}validation`,
    delete: `${BASE_URL}experiments`,
    getprocess: `${EXPURL}process_by_experiment`,
    individualProcess: `${EXPURL}process`,
    download: `${EXPURL}download`,
    goProcessDownload: `${GOPROCESSDOWNLOAD}download`,
    celeryTask: `${SOCKET_URL}celery-task`,
    beforeEda: `${BASE_URL}standardize_column_names`,
    upload: `${EXPURL}upload`,
    updateState: `${EXPURL}process/state_name/`,
    states: `${EXPURL}states`,
    getSimulation: `${SIMULATIONURL}simulation`,
    goProcess: `${GO_PROCESS_URL}`,
    goProcessStates: `${GOPROCESSSTATES}`,
    assortment: `${ASSORTMENT_URL}`,
    addExperimentMapping: (expId: string) =>
      `${BASE_URL}v3/${expId}/add/experiment-mapping`,
    getMappedExperiments: (expId: string) =>
      `${BASE_URL}v3/${expId}/mapped-experiments`,
    getCopyToExperiments: (expId: string, linkExpId: string) =>
      `${BASE_URL}v3/experiments/${expId}/copy-to/${linkExpId}`,
    experimentsInProgress: (expId: string) =>
      `${BASE_URL}v2/experiments/${expId}/status/in_progress`,

    updateExperimentOutdated: (expId: string) =>
      `${BASE_URL}v3/experiments/${expId}/update-outdated`,

    getAssortmentDetails: (expId: string) =>
      `${BASE_URL}v3/experiments/${expId}/assortment-details`,

    phoExperiments: `${PHOBASEURL}/experiments`,
    phoBaseUrl: `${PHOBASEURL}`,
    simulationReport: (expId: string) =>
      `${PHOBASEURL}/${expId}/simulation/report`,
    votingSummary: (expId: string) => `${PHOBASEURL}/${expId}/voting/summary`,
    excelDownload: (expId: string) => `${PHOBASEURL}/${expId}/summary/download`,
    phoSearchUser: `${USERS_V3_URL}users/search-entra-user`,

    sprSummary: (expId: string) => `${PHOBASEURL}/${expId}/spr/summary`,
    previousAnalysis: (expId: string) =>
      `${PHOBASEURL}/experiments/${expId}/previous-analysis`,
    affectedDashboard: (expId: string) =>
      `${PHOBASEURL}/${expId}/dashboard/affected-dashboard`,

    // Folder Structure API's
    getExperimentsList: `${EXP_BASE_URL_V3}/experiments/list`,
    createNewFolder: `${EXP_BASE_URL_V3}/folders`,
    experimentStatus: `${EXP_BASE_URL_V3}/experiments/statuses`,
    renameAndMoveFolder: (folderId: string) =>
      `${EXP_BASE_URL_V3}/folders/${folderId}`,
    deleteFolder: (folderId: string) =>
      `${EXP_BASE_URL_V3}/folders/${folderId}`,
    shareAndUpdateFolder: (folderId: string) =>
      `${EXP_BASE_URL_V3}/folders/${folderId}/share`,
    getMyFoldersExperiments: (folderId: string) =>
      `${EXP_BASE_URL_V3}/folders/${folderId}/contents`,
    getSharedFoldersExperiments: () =>
      `${EXP_BASE_URL_V3}/folders/shared-contents`,
    moveExperimentIntoFolder: (expId?: string) =>
      `${EXP_BASE_URL_V3}/experiments/${expId}`,
    bulkShareExperimentsFolders: `${EXP_BASE_URL_V3}/bulk-share`,
    bulkMoveExperimentsFolders: `${EXP_BASE_URL_V3}/bulk-move`,
    bulkDeleteExperimentsFolders: `${EXP_BASE_URL_V3}/bulk-delete`,
  },
  assortment: {
    prepareClusterData: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/clustering/prepare-cluster-data`,
    clusterProfiling: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/clustering/profiling`,
    clusteringClusters: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/clustering/clusters`,
    clusterProfilingRefresh: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/clustering/profiling/refresh`,
    level0Refresh: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/level0/refresh`,
    level0ParetoRefresh: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/level0/pareto/refresh`,
    postLevel0Pareto: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/level0/pareto`,
    getLevel0Pareto: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/level0/pareto`,
    optimization: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/optimization/pygad`,
    nesting: (expId: string) => `${ASSORTMENT_URL}${expId}/nesting`,
    refreshNesting: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/nesting/refresh`,
    simulation: (expId: string) => `${ASSORTMENT_URL}${expId}/simulation`,
    simulationReport: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/simulation/report`,
    financialImpact: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/financial-impact`,
    financialImpactPDF: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/financial-impact/pdf`,
    clustersList: (expId: string) => `${ASSORTMENT_URL}${expId}/clusters`,
    getClusterData: (expId: string, clusterId: string) =>
      `${ASSORTMENT_URL}${expId}/clusters/${clusterId}`,
    dynamicNesting: (expId: string, clusterId: string) =>
      `${ASSORTMENT_URL}${expId}/clusters/${clusterId}/dynamic-nesting`,
    // dynamicNestingPreprocessing: (expId: string, clusterId: string) =>
    //   `${ASSORTMENT_URL}${expId}/clusters/${clusterId}/dynamic-nesting/preprocessing`,
    dynamicNestingPreprocessing: (expId: string, planId: string) =>
      `${ASSORTMENT_URL}${expId}/plans/${planId}/dynamic-nesting/preprocessing`,
    downloadSeasonalityTemplate: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/templates/seasonality/download`,
    downloadStyleMatrix: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/templates/style-matrix/download`,
    downloadAssortment: () => `${SIMULATIONURL}simulation/export/excel`,

    // Advanced clustering apis
    getClusterList: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/advanced-clustering`,
    prepareBaseData: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/advanced-clustering/prepare-cluster-data`,
    clusterStoreData: (expId: string, clusterId: string) =>
      `${ASSORTMENT_URL}${expId}/advanced-clustering/${clusterId}/store-data-path`,
    modifyStoreData: (expId: string, clusterId: string) =>
      `${ASSORTMENT_URL}${expId}/advanced-clustering/${clusterId}/modify/store-data`,
    downloadFile: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/advanced-clustering/download`,
    deleteClusters: (expId: string, clusterId: string) =>
      `${ASSORTMENT_URL}${expId}/advanced-clustering/${clusterId}`,
    mergeClusters: (expId: string, clusterId: string) =>
      `${ASSORTMENT_URL}${expId}/advanced-clustering/${clusterId}/profiling/merge`,
    getColumnsForClusters: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/advanced-clustering/columns`,
    createColumnClusters: (expId: string, clusterId: string) =>
      `${ASSORTMENT_URL}${expId}/advanced-clustering/${clusterId}/profiling/column`,
    getFeaturesForClusters: (expId: string, clusterId: string) =>
      `${ASSORTMENT_URL}${expId}/advanced-clustering/${clusterId}/cluster-recommendations`,
    createFeatureClusters: (expId: string, clusterId: string) =>
      `${ASSORTMENT_URL}${expId}/advanced-clustering/${clusterId}/profiling/features`,
    refreshClusterProfiling: (expId: string, clusterId: string) =>
      `${ASSORTMENT_URL}${expId}/advanced-clustering/${clusterId}/profiling/refresh`,
    finalOutputClusterProfiling: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/advanced-clustering/profiling`,
    clusterOutput: (expId: string, clusterId: string) =>
      `${ASSORTMENT_URL}${expId}/advanced-clustering/${clusterId}/profiling`,
    suggestBins: (expId: string, clusterId: string) =>
      `${ASSORTMENT_URL}${expId}/advanced-clustering/${clusterId}/suggest-bins`,
    userUploadedCluster: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/advanced-clustering/profiling/uploaded-data`,
    migrateExperiment: (expId: string) =>
      `${ASSORTMENT_URL}${expId}/advanced-clustering/migrate-experiment`,
  },
  utility: {
    uploadImages: () => `${API_URL}utils/api/v2/upload/images`,
  },
  library: {
    getStoreLists: () => `${BASE_PRODUCT_URL}api/v3/stores`,
    addStore: () => `${BASE_PRODUCT_URL}api/v3/stores`,
    updateStore: (storeId: string) =>
      `${BASE_PRODUCT_URL}api/v3/stores/${storeId}`,
    deleteStore: (storeId: string) =>
      `${BASE_PRODUCT_URL}api/v3/stores/${storeId}`,
    checkUniqueStore: (storeId: string, key_account_id: string) =>
      `${BASE_PRODUCT_URL}api/v3/stores/check-availability?store_id=${storeId}&key_account_id=${key_account_id}`,
    downloadStoreTemplate: () =>
      `${BASE_PRODUCT_URL}api/v3/stores/bulk-uploads/template`,
    uploadStoreTemplate: () => `${BASE_PRODUCT_URL}api/v3/stores/bulk-upload`,
    createNewFixture: () => `${BASE_PRODUCT_URL}api/v3/fixtures`,
    getFixtureObjects: (fixtureId: string) =>
      `${BASE_PRODUCT_URL}api/v3/fixtures/${fixtureId}/objects`,
    getFixtureDetails: (fixtureId: string) =>
      `${BASE_PRODUCT_URL}api/v3/fixtures/${fixtureId}`,
    getFixtureVersions: (fixtureId: string) =>
      `${BASE_PRODUCT_URL}api/v3/fixtures/${fixtureId}/versions`,
    restoreFixtureVersion: (fixtureId: string, versionId: string) =>
      `${BASE_PRODUCT_URL}api/v3/fixtures/${fixtureId}/versions/${versionId}/restore`,
  },
};

const status = [
  "started",
  "not_started",
  "pending",
  "in_progress",
  "completed",
  "success",
  "failed",
];
const xStatus = {
  started: "started",
  notStarted: "not_started",
  pending: "pending",
  inProgress: "in_progress",
  completed: "completed",
  failed: "failed",
  failure: "failure",
  success: "success",
};

@Injectable({
  providedIn: "root",
})
export class StateService {
  tempElement: HTMLElement | null = null;
  public sharedProperties: { [key: string]: any } = {
    planoImages: {},
  };
  selectedtemplate: any = false;
  private level = new BehaviorSubject<any>(null);
  public Result = new BehaviorSubject<any>(null);
  private statesResetCall = new Subject<any>();
  setFileName: Subject<any> = new Subject<any>();
  private dataSubject = new BehaviorSubject<any>(null);
  data$ = this.dataSubject.asObservable();
  readonly callSetLevel = this.level.asObservable();
  createNewExp = false;
  guideLayerShow = false;
  hashChanged = false;
  apiList = apiData;
  websocketConfig = websocketConfig;
  status = status;
  xStatus = xStatus;
  isDownload = false;
  calledApiList: any = [];
  setTrafficFlow: Subject<any> = new Subject();
  fileValidationData: Subject<any> = new Subject();
  errorMessage = ["Something went wrong, Please try again"];
  private userDetails: { [key: string]: any } | null = null;
  permissions = {
    tag_user_role: "users.roles.tag",
    untag_user_role: "users.roles.untag",

    admin_users_list: "admin.users.list",
    admin_requests_view: "admin.requests.view",
    admin_requests_approve: "admin.requests.approve",

    zone_users_list: "zones.users.list",
    zone_users_add: "zones.users.add",
    zone_users_migrate: "zones.users.migrate",
    zone_users_revoke: "zones.users.revoke",

    zone_users_key_account_sync: "zones.users.key-accounts.sync",
    zone_requests_view: "zones.requests.view",
    zone_requests_approve: "zones.requests.approve",

    zones_add: "zones.add",
    zones_edit: "zones.edit",
    zones_delete: "zones.delete",

    key_accounts_add: "key-accounts.add",
    key_accounts_edit: "key-accounts.edit",
    key_accounts_delete: "key-accounts.delete",

    roles_add: "roles.add",
    roles_edit: "roles.edit",
    roles_delete: "roles.delete",

    permissions_add: "permissions.add",
    permissions_edit: "permissions.edit",
    permissions_delete: "permissions.delete",
  };
  process = {
    pending: "(1/3) Data processing is not started yet",
    in_progress: "(2/3) Data processing is started",
    completed: "(3/3) Fetching result",
    failed: "Data processing is failed",
    dotLoader: "...",
  };
  searchCols: any = {
    cluster_output: [
      { key: "cluster_name", dropDown: true, sort: true },
      { key: "cluster_id", dropDown: true, sort: true },
      { key: "poc_id", textSearch: true, sort: true },
      { key: "address", textSearch: true, sort: true },
      { key: "latitude", textSearch: true, sort: true },
      { key: "longitude", textSearch: true, sort: true },
      { key: "store_name", textSearch: true, sort: true },
      { key: "revenue", textSearch: true, sort: true },
      { key: "volume", textSearch: true, sort: true },
      { key: "units_sold", textSearch: true, sort: true },
    ],

    level0_output: [
      { key: "Cluster_Parent_SKU", textSearch: true },
      { key: "cluster_name", dropDown: true },
      { key: "category", dropDown: true },
      { key: "style", dropDown: true },
      { key: "discontinued", dropDown: true },
      { key: "price_anomaly", dropDown: true },
      { key: "low_abs_vol", dropDown: true },
      { key: "seasonal", dropDown: true },
      { key: "distribution", dropDown: true },
      { key: "recent_data", dropDown: true },
      { key: "lack_of_data", dropDown: true },
      { key: "SKU_funnel_flag", dropDown: true },
    ],

    pareto_output: [
      { key: "cluster_name", dropDown: true },
      { key: "category", dropDown: true },
      { key: "style", dropDown: true },
      { key: "brand_category", dropDown: true },
      { key: "ptc_segment", dropDown: true },
      { key: "Cluster_Parent_SKU", textSearch: true },
      { key: "pareto_level", dropDown: true },
      { key: "pareto_flag", dropDown: true },
      { key: "Total_Volume", numberSearch: true },
      { key: "Cumulative_Volume", numberSearch: true },
      { key: "cumulative_%", numberSearch: true },
      { key: "volume_contribution_%", numberSearch: true },
      { key: "volume", numberSearch: true },
    ],

    nesting_output: [
      { key: "cluster_name", dropDown: true },
      { key: "Cluster_Parent_SKU", textSearch: true },
      { key: "nest_no", textSearch: true },
      { key: "brand_category", dropDown: true },
      { key: "ptc_segment", dropDown: true },
      { key: "category", dropDown: true },
      { key: "Parent_SKU", textSearch: true },
      { key: "price_per_ltr", textSearch: true },
      { key: "ROS", textSearch: true },
      { key: "pack_size_bins", dropDown: true },
      { key: "price_bins", dropDown: true },
      { key: "ROS_bins", dropDown: true },
      { key: "pack_size", dropDown: true },
      { key: "pack_type", dropDown: true },
      { key: "volume", numberSearch: true },
      { key: "revenue", numberSearch: true },
      { key: "no_of_stores", numberSearch: true },
      { key: "pack_qty", dropDown: true },
      { key: "style", dropDown: true },
      { key: "cluster_id", dropDown: true },
      { key: "pack_type_bins", dropDown: true },
      { key: "pack_qty_bins", dropDown: true },
    ],

    optimization_output: [
      { key: "Cluster_Parent_SKU", textSearch: true },
      { key: "category", dropDown: true },
      { key: "cluster_name", dropDown: true },
      { key: "sku", textSearch: true },
      { key: "sku_detail", textSearch: true },
      { key: "brand_category", dropDown: true },
      { key: "ptc_segment", dropDown: true },
      { key: "manufacturer", dropDown: true },
      { key: "abi_vs_others", dropDown: true },
      { key: "pack_size", dropDown: true },
      { key: "pack_type", dropDown: true },
      { key: "volume", numberSearch: true },
      { key: "revenue", numberSearch: true },
      { key: "no_of_stores", numberSearch: true },
      { key: "Module", dropDown: true },
      { key: "model_color", dropDown: true },
      { key: "cultivate_flag", dropDown: true },
      { key: "final_color", dropDown: true },
      { key: "Final Color", dropDown: true },
      { key: "size", dropDown: true },
      { key: "style", dropDown: true },
      { key: "cluster_id", dropDown: true },
      { key: "Reason", dropDown: true },
      { key: "model_output", dropDown: true },
    ],

    simulation_output: [
      { key: "Cluster_Parent_SKU", textSearch: true },
      { key: "category", dropDown: true },
      { key: "cluster_name", dropDown: true },
      { key: "sku", textSearch: true },
      { key: "sku_detail", textSearch: true },
      { key: "brand_category", dropDown: true },
      { key: "ptc_segment", dropDown: true },
      { key: "manufacturer", dropDown: true },
      { key: "abi_vs_others", dropDown: true },
      { key: "pack_size", dropDown: true },
      { key: "pack_type", dropDown: true },
      { key: "volume", textSearch: true },
      { key: "revenue", textSearch: true },
      { key: "no_of_stores", numberSearch: true },
      { key: "Module", dropDown: true },
      { key: "model_color", dropDown: true },
      { key: "cultivate_flag", dropDown: true },
      { key: "final_color", dropDown: true },
      { key: "Final Color", dropDown: true },
      { key: "size", dropDown: true },
      { key: "style", dropDown: true },
      { key: "cluster_id", dropDown: true },
      { key: "Reason", dropDown: true },
      { key: "model_output", dropDown: true },
      { key: "simulate_mix", dropDown: true },
    ],
  };
  monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  country: any;
  keyacc: any;
  downloadProgressForSimulation = new BehaviorSubject<any>(null);
  openSoftWarningToast = new BehaviorSubject<any>(null);
  downloadProgressForPDF = new BehaviorSubject<any>(null);
  downloadTriggered = new Subject<any>();
  pdfGeneration = new BehaviorSubject<any>(null);
  individualProcess: any;
  zoomType = new BehaviorSubject<any>(null);
  assortDateRange: null | undefined | string;
  region: null | undefined | string;
  levelsKeyOrder: any = ["Country", "State", "Region", "Channel"];
  private _lockAssortment$ = new BehaviorSubject(null);
  kbFactor = 1e3;
  mbFactor = 1e6;
  gbFactor = 1e9;
  allowedFileType: any = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "application/wps-office.xlsx",
    "text/csv",
  ];
  setStatesCall: boolean = false;
  setStatesList: any = [];
  experimentTypes: any = [
    "assortment",
    "space-assortment",
    "data-exploration",
    "clustering",
    "pho",
    "global-dashboard",
  ];

  private pinnedWidgetsSubject = new BehaviorSubject<any[]>(
    this.load("pinnedWidget")
  );
  private savedWidgetsSubject = new BehaviorSubject<any[]>(
    this.load("savedWidget")
  );
  private pinnedWidgetsDataSubject = new BehaviorSubject<any[]>([]);

  pinnedWidgets$ = this.pinnedWidgetsSubject.asObservable();
  pinnedWidgetsData$ = this.pinnedWidgetsDataSubject.asObservable();
  savedWidgets$ = this.savedWidgetsSubject.asObservable();

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<CommonModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private http: HttpService,
    private localStorageService: LocalStorageService,
    private router: Router
  ) {
    this.keyacc = sessionStorage.getItem("expKeyAccount");
    this.country = sessionStorage.getItem("expCountry");
    this.region = sessionStorage.getItem("expRegion");
    this.assortDateRange = sessionStorage.getItem("assortDateRange");
  }

  setUser(user: { [key: string]: any }) {
    this.userDetails = user;
  }

  getUser(): { [key: string]: any } {
    if (!this.userDetails) {
      this.setUser(JSON.parse(localStorage.getItem("user-name")!));
    }

    return this.userDetails as { [key: string]: any };
  }

  isBetaUser(): boolean {
    return Boolean(this.getUser()?.is_beta_user);
  }

  get getAssortmentLock(): any {
    return this._lockAssortment$;
  }

  setAssortmentLock(newValue: any) {
    this._lockAssortment$.next(newValue);
  }

  checkAppConfig() {
    // First check if we can determine domain from URL (for production environments)
    const hostname = window.location.hostname;

    if (hostname.includes("ai-imagery")) {
      return "ai-imagery";
    } else if (hostname.includes("catexpert")) {
      return "catexpert";
    }

    // Fallback to config file (for development environments)
    const configApp = config?.app || "catexpert";
    return configApp;
  }

  getAppDetails() {
    return config;
  }

  addCalledApi(url: string) {
    setTimeout(() => {
      this.calledApiList.push(url);
    }, 0);
  }

  removeCalledApi(url: string) {
    setTimeout(() => {
      this.calledApiList.splice(this.calledApiList.indexOf(url), 1);
    }, 0);
  }
  popOutCalledApi() {
    setTimeout(() => {
      this.calledApiList.pop();
    }, 0);
  }

  showMessage(options: any): void {
    const dialogRef = this.dialog.open(CommonModalComponent, {
      data: {
        ...options,
        callback: (data: any) => {
          this.dialog.closeAll();
        },
      },
      width: "600px",
      maxHeight: "inherit",
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {});
  }

  dialogClose() {
    this.dialog.closeAll();
  }

  orderByNumber = (list: any = []) =>
    list.sort(
      (a: { order: number }, b: { order: number }) => a.order - b.order
    );

  getMethodName = (key: string = "") =>
    key &&
    typeof key === "string" &&
    key
      .split("-")
      .map((item, index) =>
        index === 0 ? item : item.charAt(0).toUpperCase() + item.substr(1)
      )
      ?.join("");

  titleCase = (text: string = "") => {
    return (
      text &&
      typeof text === "string" &&
      text
        ?.split("_")
        .map((item) => item.charAt(0).toUpperCase() + item.substr(1))
        ?.join(" ")
    );
  };

  titleRemoveUnderscore = (text: string = "") =>
    text &&
    typeof text === "string" &&
    text?.split("_").join(" ").toLowerCase();

  titleFirstLetterCaps = (text: string = "") =>
    text &&
    typeof text === "string" &&
    text
      .split("_")
      .map((item, index) =>
        index === 0 ? item.charAt(0).toUpperCase() + item.substr(1) : item
      )
      ?.join(" ");

  expTypeTransform(value: string) {
    if (!value) {
      return "";
    }
    return value.replace(/[\s_]+/g, "-").toLowerCase();
  }

  staticPayload(
    experiment_id: any,
    proProcssing: any = {},
    featureList: any = [],
    noOfClustering: any = "",
    country: string = "",
    keyacc: string = "",
    region: string = "",
    assortDateRange: string = ""
  ) {
    return {
      prepareClusterData: {
        state_id: "prepare-cluster-data",
        experiment_id: experiment_id,
        meta: {
          id: "prepare-cluster-data",
          queue: "clustering-queue",
          args: [
            experiment_id,
            proProcssing,
            country,
            keyacc,
            region,
            assortDateRange,
          ],
          kwargs: {},
        },
      },
      clusters: {
        state_id: "clusters",
        experiment_id: experiment_id,
        meta: {
          id: "clusters",
          queue: "clustering-queue",
          args: [experiment_id, featureList],
          kwargs: {},
        },
      },
      profiling: {
        state_id: "profiling",
        experiment_id: experiment_id,
        meta: {
          id: "profiling",
          queue: "clustering-queue",
          args: [
            experiment_id,
            featureList,
            parseInt(noOfClustering),
            true,
            null,
            keyacc,
            country,
            region,
            assortDateRange,
          ],
          kwargs: {},
        },
      },
      profilingWithoutStore: {
        state_id: "profiling",
        experiment_id: experiment_id,
        meta: {
          id: "profiling",
          queue: "clustering-queue",
          args: [
            experiment_id,
            [],
            0,
            false,
            null,
            keyacc,
            country,
            region,
            assortDateRange,
          ],
          kwargs: {},
        },
      },
      refreshProfiling: {
        state_id: "refresh-profiling",
        experiment_id: experiment_id,
        meta: {
          id: "refresh-profiling",
          queue: "clustering-queue",
          args: [experiment_id],
          kwargs: {},
        },
      },
    };
  }

  preProcessing() {
    return [
      {
        key: "size",
        option: ["pivot"],
      },
      {
        key: "pack_type",
        option: ["pivot"],
      },
      {
        key: "ptc_segment",
        option: ["pivot"],
      },
    ];
  }

  getHeader(data: any = []) {
    const head: any = [];
    data.forEach((element: any) => {
      for (let key in element) {
        if (head.indexOf(key) === -1) {
          head.push(key);
        }
      }
    });
    return head;
  }

  getHeaderWithFilters(data: any = [], filter: any = []) {
    const head: any = [];
    data.forEach((element: any) => {
      for (let key in element) {
        if (head.map((e: any) => e.title).indexOf(key) === -1) {
          const search =
            (Array.isArray(filter) &&
              filter?.find((item: any) => item?.key === key)) ||
            {};
          let dropDownValues: any[] = [];
          if (search.dropDown) {
            dropDownValues = [...new Set(data.map((item: any) => item[key]))];
          }
          head.push({
            title: key,
            textSearch: search?.textSearch || false,
            dropDown: search?.dropDown || false,
            numberSearch: search?.numberSearch || false,
            numberValues: search?.numberSearch ? ["<=", ">="] : [],
            dropDownValues: search?.dropDown ? dropDownValues : [],
            sortOrder: "sort",
            show: true,
          });
        }
      }
    });
    return head;
  }

  getTableHeaderWithFilters(data: any = [], filter: any = []) {
    const head: any = [];
    data.forEach((element: any) => {
      for (let key in element) {
        if (head.map((e: any) => e.value).indexOf(key) === -1) {
          const search =
            (Array.isArray(filter) &&
              filter?.find((item: any) => item?.key === key)) ||
            {};
          let dropDownValues: any[] = [];
          if (search.dropDown) {
            dropDownValues = [...new Set(data.map((item: any) => item[key]))];
          }

          head.push({
            label: this.titleCase(key),
            value: key,
            valueType: "",
            filterType: search?.textSearch
              ? "input_text"
              : search?.dropDown
                ? "dropdown"
                : "",
            filterOptions: [],
            filterMatchMode: search?.textSearch ? "contains" : "",
            sortable: search?.sort || true,
          });
        }
      }
    });
    return head;
  }

  nFormatter(num: number) {
    if (+num >= 1000000000) {
      return (+num / 1000000000).toFixed().replace(/\.0$/, "") + "B";
    }
    if (+num >= 1000000) {
      return (+num / 1000000).toFixed().replace(/\.0$/, "") + "M";
    }
    if (+num >= 1000) {
      return (+num / 1000).toFixed().replace(/\.0$/, "") + "K";
    }
    return num;
  }

  nFormatterDecimal(num: number) {
    const isNegative = num < 0;
    num = Math.abs(num); // Take the absolute value

    let formattedNum;
    if (num >= 1000000000) {
      formattedNum = (num / 1000000000).toFixed(1) + "B";
    } else if (num >= 1000000) {
      formattedNum = (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      formattedNum = (num / 1000).toFixed(1) + "K";
    } else {
      formattedNum = num.toString();
    }

    return isNegative ? "-" + formattedNum : formattedNum;
  }

  nFormatterVolume(num: number, type: string) {
    const isNegative = num < 0;
    num = Math.abs(num); // Take the absolute value

    let formattedNum;
    if (type === "Total Volume" || type === "Volume") {
      if (num >= 1000000000) {
        formattedNum =
          (num / 1000000000).toFixed().replace(/\.0$/, "") + "B HL";
      } else if (num >= 1000000) {
        formattedNum = (num / 1000000).toFixed().replace(/\.0$/, "") + "M HL";
      } else if (num >= 1000) {
        formattedNum = (num / 1000).toFixed().replace(/\.0$/, "") + "K HL";
      } else {
        formattedNum = num.toString();
      }
    } else {
      formattedNum = num.toString();
    }

    return isNegative ? "-" + formattedNum : formattedNum;
  }

  nFormatterVolumeDecimal(num: number, type: string) {
    const isNegative = num < 0;
    num = Math.abs(num); // Take the absolute value

    let formattedNum;
    if (type === "Total Volume") {
      if (num >= 1000000000) {
        formattedNum = (num / 1000000000).toFixed(1) + "B HL";
      } else if (num >= 1000000) {
        formattedNum = (num / 1000000).toFixed(1) + "M HL";
      } else if (num >= 1000) {
        formattedNum = (num / 1000).toFixed(1) + "K HL";
      } else {
        formattedNum = num.toString();
      }
    } else {
      formattedNum = num.toString();
    }

    return isNegative ? "-" + formattedNum : formattedNum;
  }

  nParseFloat(num: number) {
    return num.toFixed(2);
  }

  sortObject(obj: any) {
    // Get an array of the keys:
    let keys = Object.keys(obj);

    // Then sort by using the keys to lookup the values in the original object:
    keys.sort((a, b) => obj[b] - obj[a]);

    return keys;
  }

  formatPayload(
    state_id: string,
    experiment_id: string,
    id: string,
    queue: string,
    args: any,
    kwargs: any = {}
  ) {
    return {
      state_id: state_id,
      experiment_id: experiment_id,
      meta: {
        id: id,
        queue: queue,
        args: args,
        kwargs: kwargs,
      },
    };
  }

  getStateArgs(state_id: string) {
    let stateArgs: any = [];
    const allState =
      (localStorage.allState && JSON.parse(localStorage.allState)) || [];
    allState.forEach((element: any) => {
      if (
        element?.name === state_id &&
        element.meta_schema?.args?.datatype === "list"
      ) {
        stateArgs = element.meta_schema?.args?.params || [];
      }
    });
    return stateArgs;
  }

  getSearchCols(key: string) {
    return this.searchCols[key] || [];
  }

  filterValues(
    originalTableData: any = [],
    formValue: any = {},
    tableName: any = "level0_output"
  ) {
    const tableFields = this.getSearchCols(tableName);
    return originalTableData.filter((element: any) => {
      let isPresent = true;

      for (let key in formValue) {
        if (
          formValue[key] === "" ||
          formValue[key] === null ||
          formValue[key] === undefined
        ) {
          isPresent = true;
          continue;
        }

        const tableKey = tableFields.find((item: any) => item.key === key);

        if (typeof element?.[key] === "number" && tableKey?.number) {
          isPresent = element[key] >= parseInt(formValue[key]);
        }

        if (tableKey?.dropDown) {
          if (element[key] === formValue[key]) {
            isPresent = true;
          } else {
            isPresent = false;
          }
        }

        if (tableKey?.textSearch) {
          if (typeof element?.[key] === "string") {
            isPresent = element[key]
              .toLowerCase()
              .includes(formValue[key].toLowerCase());
          } else if (typeof element?.[key] === "number") {
            isPresent = element[key].toString().includes(formValue[key]);
          }
        }

        if (
          tableKey?.numberSearch &&
          formValue[`${key}_numberSearchOperator`] &&
          formValue[key] !== null
        ) {
          if (formValue[`${key}_numberSearchOperator`] == ">=") {
            isPresent = parseInt(element[key]) >= parseInt(formValue[key]);
          } else {
            isPresent = parseInt(element[key]) <= parseInt(formValue[key]);
          }
        }

        if (!isPresent) {
          break;
        }
      }

      return isPresent;
    });
  }

  showClearAll(values: any = {}) {
    let isShow = false;
    for (let key in values) {
      if (values[key]?.toString()) {
        isShow = true;
      }
    }
    return isShow;
  }

  setTokenExpiryDate() {
    window.localStorage.removeItem("tokenExpiryDate");

    let tokenExpiryDuration: number = ACCESS_TOKEN_EXPIRY_DURATION;
    let currentDate = new Date();
    let tokenExpiryDate = new Date(currentDate.getTime());

    tokenExpiryDate.setMinutes(currentDate.getMinutes() + tokenExpiryDuration);
    window.localStorage.setItem("tokenExpiryDate", tokenExpiryDate.toString());
  }

  getTokenExpiryDate() {
    return new Date(Date.parse(window.localStorage.tokenExpiryDate));
  }

  getToken() {
    return window.localStorage.c;
  }

  getRefreshToken(): string | null {
    return window.localStorage.d;
  }

  getProcessByExpId() {
    setTimeout(() => {
      const expId = sessionStorage.getItem("expId");
      const url = `${this.apiList.experiments.getprocess}/${expId}/self`;
      this.http.getMethod(url).subscribe(
        (val: any) => {
          if (val) {
            const states = val.map((item: any) => ({
              order: item.order,
              status: item.status,
              state_id: item.state_id,
            }));

            sessionStorage.setItem("process", JSON.stringify(states));
            this.updatedProcess(states);
          }
        },
        (err: any) => {}
      );
    }, 300);
  }

  refreshToken() {
    const url = `${this.apiList.user.refreshToken}`;
    return this.http.getMethod(url);
  }

  updatedProcess(states: any) {
    this.level.next(states);
  }
  openToastService(result: any) {
    this.Result.next(result);
  }
  setDownloadProgress(status: any) {
    this.downloadProgressForSimulation.next(status);
  }
  setPDFDownloadProgress(status: any) {
    this.downloadProgressForPDF.next(status);
  }
  triggerDownloadService() {
    this.downloadTriggered.next("trigger");
  }
  //planogram services
  createPlanogram(params: any) {
    const url = `${this.apiList.experiments.getExperiments}`;
    return this.http.postMethod(url, params);
  }

  saveShelfDetailsService(url: any, params: any) {
    return this.http.postMethod(url, params);
  }

  // API to fetch templates list
  getTemplatesList(options: any) {
    const url = this.apiList.templates.getTemplates(options);
    return this.http.getMethod(url);
  }

  // API to get template details
  getTemplateDetails(templateId: string) {
    const url = this.apiList.templates.getTemplateDetails(templateId);
    return this.http.getMethod(url);
  }

  saveTemplate(params: any) {
    const url = this.apiList.planogram.saveTemplate();
    return this.http.postMethod(url, params);
  }

  saveExistingPlanShelfService(url: any, params: any) {
    return this.http.putMethod(url, params);
  }
  // API to fetch the Plans
  getPlansOfPlanogram(expId: string) {
    const url = this.apiList.planogram.getPlansOfPlanogram(expId);
    return this.http.getMethod(url);
  }

  // API to fetch the Plans
  getPlanogramList(payload: any) {
    const url = this.apiList.planogram.getPlanogramList;
    return this.http.postMethod(url, payload);
  }

  // API for Create Plan with leftout skus
  createPlanWithLeftOutSkus(expId: string) {
    const url = this.apiList.planogram.getPlansOfPlanogram(expId);
    return this.http.postMethod(url, {});
  }

  // API to fetch products of a specific plan
  getItemsForAPlan(expId: string, planId: string, version: any = null) {
    const url = this.apiList.planogram.getItemsForAPlan(expId, planId);
    if (version !== null) {
      return this.http.getMethod(url + "?version_id=" + version);
    }
    return this.http.getMethod(url);
  }

  // API to sync products of a specific plan
  syncProductChanges(
    expId: string,
    planId: string,
    data: { products: ProductSyncData[] }
  ) {
    const url = this.apiList.planogram.syncPlanProducts(expId, planId);
    return this.http.putMethod(url, data);
  }

  // API to delete all products of a specific plan
  deleteAllProducts(expId: string, planId: string) {
    const url = this.apiList.planogram.deleteAllProducts(expId, planId);
    return this.http.deleteMethod(url);
  }

  planoFileUploadProcessStatus(expId: string) {
    return this.http.getMethod(this.apiList.planogram.processCheck(expId));
  }

  getPlanoLeftoutItems(expId: string) {
    return this.http.getMethod(this.apiList.planogram.getLeftout(expId));
  }

  updateItemsOfShelfCell(
    expId: string,
    planId: string,
    data: Partial<UpdateShelfProductsPayload>
  ) {
    const url = this.apiList.planogram.updateCellItems(expId, planId);
    return this.http.putMethod(url, data);
  }

  bulkUpdateShelfItems(
    expId: string,
    planId: string,
    data: Partial<BulkUpdateShelfProductsPayload>
  ) {
    const url = this.apiList.planogram.bulkUpdateCellItems(expId, planId);
    return this.http.putMethod(url, data);
  }

  resetPlanogram(expId: string, planId: string) {
    const url = this.apiList.planogram.resetPlano(expId, planId);
    return this.http.postMethod(url);
  }

  createPlanoFromLeftout(expId: string) {
    const url = this.apiList.planogram.planoFromLeftout(expId);
    return this.http.postMethod(url);
  }

  getSystemGenItemsForAPlan(
    expId: string,
    planId: string,
    version: any = null
  ) {
    const url = this.apiList.planogram.getSystemGenItemsForAPlan(expId, planId);
    if (version !== null) {
      return this.http.getMethod(url + "?version_id=" + version);
    }
    return this.http.getMethod(url);
  }

  //planogram final services

  clearPlanoV2LocalStorage() {
    sessionStorage.removeItem("plansDetails");
    sessionStorage.removeItem("selectedPlanogramDetails");
    sessionStorage.removeItem("selectedPlanOfPlano");
    sessionStorage.removeItem("experimentMode");
  }

  getExistingPlan(url: any) {
    return this.http.getMethod(url);
  }

  createPlans(url: any, params: any) {
    return this.http.postMethod(url, params);
  }
  getPlan(expId: string, planId: string, version: string = "v1") {
    const url = this.apiList.planogram.getPlanOfPlano(expId, planId, version);
    return this.http.getMethod(url);
  }

  getSystemPlan(expId: string, planId: string, version: any = null) {
    const url = this.apiList.planogram.getSystemPlanOfPlano(expId, planId);
    if (version !== null) {
      return this.http.getMethod(url + "?version_id=" + version);
    }
    return this.http.getMethod(url);
  }

  deletePlanFromPlanogram(expId: string, planId: string) {
    const url = this.apiList.planogram.deletePlanFromPlanogram(expId, planId);
    return this.http.deleteMethod(url);
  }

  updatePlanShelfDimension(expId: string, planId: string, params: any) {
    const url = this.apiList.planogram.updatePlanOfShelfDimension(
      expId,
      planId
    );
    return this.http.putMethod(url, params);
  }
  updatePlanSegmentAllocation(expId: string, planId: string, params: any) {
    const url = this.apiList.planogram.updatePlanOfSegmentAllocation(
      expId,
      planId
    );
    return this.http.putMethod(url, params);
  }
  updatePlanGrid(expId: string, planId: string, params: any) {
    const url = this.apiList.planogram.updatePlanGrid(expId, planId);
    return this.http.putMethod(url, params);
  }
  getCsvForDownloadTemplate() {
    const url = this.apiList.planogram.downloadSampleTemplate;
    return this.http.getBlobMethod(url);
  }
  getPSAFileForPlanogram(
    expId: string,
    planId: string,
    fileType?: string,
    version?: string
  ) {
    const url =
      fileType === "pln"
        ? this.apiList.planogram.GetPLNOfPlanogram(expId, planId, version)
        : this.apiList.planogram.GetPSAOfPlanogram(expId, planId, version);
    return this.http.postMethod(url);
  }
  getExcelFileForPlanogram(
    expId: string,
    planId: string,
    allPlans: boolean = false,
    version: string = "v1"
  ) {
    const url = allPlans
      ? this.apiList.planogram.getExcelOfAllPlanograms(expId, version)
      : this.apiList.planogram.GetExcelOfPlanogram(expId, planId, version);
    return this.http.postMethod(url, {});
  }
  getAllPSAFileForPlanogram(
    expId: string,
    fileType?: string,
    version?: string
  ) {
    const url =
      fileType === "pln"
        ? this.apiList.planogram.getAllPLNOfPlanogram(expId, version)
        : this.apiList.planogram.getAllPSAOfPlanogram(expId, version);
    return this.http.postMethod(url);
  }
  getLeftoutForPlanogram(expId: string) {
    const url = this.apiList.planogram.GetLeftoutExcelOfPlanogram(expId);
    return this.http.getBlobMethod(url);
  }
  getPDFForPlanogram(expId: any, planId: any, data: any) {
    const url = this.apiList.planogram.GetPDFOfPlanogram(expId, planId);
    return this.http.postMethod(url, data);
  }

  //open Soft warning toast
  openSoftWarningToastService(message: any) {
    let marginClass = sessionStorage.getItem("toastMarginClass");
    let openToastInfo: any = {
      message: message,
      marginClass: marginClass,
    };
    this.openSoftWarningToast.next(openToastInfo);
  }
  getBulkUserDetailsService(payload: any) {
    const url = this.apiList.user.sharedWith;
    return this.http.postMethod(url, payload);
  }

  connectDBProcessCheck(expId: string) {
    return this.http.getMethod(this.apiList.planogram.processCheck(expId));
  }
  //terms and conditions
  acceptTermsAndondition(params: any) {
    const url = this.apiList.user.acceptTerms;
    return this.http.putMethod(url, params);
  }

  // TODO# Temporary Code need to create the one global method and re-use
  getExperimentDetailsByExpId(expID?: string) {
    const expId = expID || sessionStorage.getItem("expId");
    const url = `${this.apiList.experiments.getExperiments}/${expId}`;
    return this.http.getMethod(url);
  }
  getZones() {
    const url = this.apiList.admin.zones;
    return this.http.getMethod(url);
  }

  getSidebarStatesByExpId(refId?: any) {
    const expId = sessionStorage.getItem("expId");
    const url = `${this.apiList.experiments.goProcessStates}${refId ? refId : expId}/states`;
    return this.http.getMethod(url);
  }

  getProductFiltersService(expId: any, params: any) {
    const url = this.apiList.planogram.getFiltersOfPlanogram(expId);
    return this.http.postMethod(url, params);
  }

  getProductOnFiltersService(expId: any, params: any) {
    const url = this.apiList.planogram.getProductsOnFilters(expId);
    return this.http.postMethod(url, params);
  }

  getFileName(filename: any, extension: any) {
    let expDetails = JSON.parse(sessionStorage.getItem("expDetails")!)[0];
    if (expDetails?.keyAccount) {
      expDetails.keyAccount = expDetails.keyAccount.split(" ").join("_");
    }
    expDetails.expName = expDetails.expName.split(" ").join("_");
    let date = new Date().toISOString().substring(0, 10).split("-").join("");
    if (expDetails?.keyAccount) {
      expDetails.keyAccount = expDetails.keyAccount.split(" ").join("_");
      return (
        expDetails.expCountry +
        "_" +
        expDetails.keyAccount +
        "_" +
        expDetails.expName +
        "_" +
        filename +
        "_" +
        date +
        "." +
        extension
      );
    } else {
      return (
        expDetails.expCountry +
        "_" +
        expDetails.expName +
        "_" +
        filename +
        "_" +
        date +
        "." +
        extension
      );
    }
  }

  //for setting color code for different segments / styles of planogram
  setSegmentOrStyleColors(planResponse: any) {
    let segmentOrStyleColors: any = {};
    if (planResponse && planResponse.market_shares) {
      for (let basis in planResponse.market_shares) {
        for (let item in planResponse.market_shares[basis]) {
          if (planResponse.market_shares[basis][item]) {
            planResponse.market_shares[basis][item].sort((a: any, b: any) => {
              return b.market_share - a.market_share;
            });
            segmentOrStyleColors[item] = {};
            planResponse.market_shares[basis][item].forEach(
              (segment: any, index: any) => {
                if (segment.label == "PROMOTION") {
                  segmentOrStyleColors[item]["INNOVATION"] = "#FBD3ED";
                } else if (segment.label == "INNOVATION") {
                  segmentOrStyleColors[item][segment.label] = "#DCDCDC";
                } else {
                  segmentOrStyleColors[item][segment.label] =
                    SEGMENT_STYLE_COLORS_ARRAY[index];
                }
              }
            );
            //setting colors for promotion and innovation if is not present in market share
            segmentOrStyleColors[item]["PROMOTION"] = "#DCDCDC";
            segmentOrStyleColors[item]["INNOVATION"] = "#FBD3ED";
          }
        }
      }
      sessionStorage.setItem(
        "segmentOrStyleColors",
        JSON.stringify(segmentOrStyleColors)
      );
      return segmentOrStyleColors;
    }
  }
  getOverallManufacturerShareOfPlanogram(expId: any) {
    const url = this.apiList.planogram.getOverallManufacturerShare(expId);
    return this.http.getMethod(url);
  }
  getTaskIdForImport(
    expId: any,
    FileToUpload: FormData,
    version: string = "v2",
    fileType?: string
  ) {
    const url = this.apiList.planogram.getImportTaskId(
      expId,
      version === "v3" ? "v3" : "v2",
      fileType === "PLN" ? "pln" : "psa"
    );
    return this.http.postFileMethod(url, FileToUpload);
  }

  assortmentProcessApi(task_id: any) {
    const url = "../../assets/assortment-mock/process-response.json";
    return this.http.getMethod(url);
  }

  getExistingPlanV2(url: any, params: any) {
    return this.http.postMethod(url, params);
  }

  getAllPlansAndUploadStatusV2(url: any) {
    return this.http.getMethod(url);
  }

  getProductForAllPlans(expId: any) {
    const url = this.apiList.planogram.getAllPlansProducts(expId);
    return this.http.getMethod(url);
  }

  // postInitExperiment(exp_Id: string, state: string) {
  //   const payload = {
  //     experiment_type: state,
  //   };
  //   const url = `${this.apiList.experiments.assortment}experiments/${exp_Id}/init`;
  //   return this.http.postMethod(url, payload);
  // }

  createProcessUrl(task_id: string) {
    return `${this.apiList.experiments.goProcess}/${task_id}/status`;
  }

  // Formatting logic for Volume
  formatVolumeLabel(num: number): string {
    if (num < 0) {
      // If the number is negative, remove the sign and format the absolute value
      return "- " + this.formatVolumeLabel(-num);
    }

    if (+num >= 1000000000 && +num <= 999999999999) {
      return (+num / 1000000000).toFixed(1) + "B HL";
    }
    if (+num >= 1000000 && +num < 999999999) {
      return (+num / 1000000).toFixed(1) + "M HL";
    }
    if (+num >= 1000 && +num < 999999) {
      return (+num / 1000).toFixed(1) + "K HL";
    }
    return `${num}`;
  }

  // Formatting logic for Volume
  formatTrendVolumeLabel(num: number): string {
    if (num < 0) {
      // If the number is negative, remove the sign and format the absolute value
      return "- " + this.formatRevenueLabel(-num);
    }
    if (num >= 1000000000) {
      return `${(num / 1000000000).toFixed(1)} B`;
    }
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)} M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)} K`;
    }
    return `${num}`;
  }

  // Formatting logic for Revenue and Maco
  formatRevenueLabel(num: any): string {
    if (num < 0) {
      // If the number is negative, remove the sign and format the absolute value
      return "- " + this.formatRevenueLabel(-num);
    }
    if (num >= 1000000000) {
      return `$${(num / 1000000000).toFixed(1)} B`;
    }
    if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(1)} M`;
    }
    if (num >= 1000) {
      return `$${(num / 1000).toFixed(1)} K`;
    }
    return `$${num}`;
  }

  sendStatesCall(apiFlag: boolean) {
    this.statesResetCall.next(apiFlag);
  }

  getStatesCall() {
    return this.statesResetCall.asObservable();
  }

  getStatesList(states: any) {
    this.setStatesList = states;
  }

  checkStatesFlag(routerSlug: string) {
    this.setStatesList.map((item: any) => {
      if (item.slug === routerSlug) {
        this.setStatesCall = item.status === "completed" ? true : false;
      }
    });
    return this.setStatesCall;
  }

  getStatesFlag(routerSlug: string) {
    this.setStatesList.map((item: any) => {
      if (item.slug === routerSlug) {
        this.setStatesCall = item.status === "completed" ? true : false;
      }
    });
    return this.setStatesCall;
  }

  parseFileSize(sizeInBytes: any) {
    let factor =
      sizeInBytes > this.gbFactor
        ? this.gbFactor
        : sizeInBytes > this.mbFactor
          ? this.mbFactor
          : this.kbFactor;
    let suffix =
      factor == this.gbFactor ? " GB" : factor == this.mbFactor ? " MB" : " KB";
    let size = sizeInBytes / factor;
    return String(parseFloat(size.toFixed(1))) + suffix;
  }

  setDockView(value: string = "0") {
    sessionStorage.setItem("dockView", value);
  }

  checkExperimentType() {
    let expType = sessionStorage.getItem("expType") || "";
    return this.expTypeTransform(expType) || null;
  }

  //service to export json data to excel
  exportToExcel(excelHeader: any[], excelData: any[], fileName: string): void {
    let ws: XLSX.WorkSheet;
    let merges: XLSX.Range[] = [];
    switch (fileName) {
      case "summaryTable":
        ws = XLSX.utils.aoa_to_sheet(excelData);
        merges.push(
          {
            s: { r: 0, c: 1 },
            e: { r: 0, c: 3 },
          },
          {
            s: { r: 0, c: 4 },
            e: { r: 0, c: 7 },
          }
        );
        break;

      case "portfolioHealthChart":
        ws = XLSX.utils.aoa_to_sheet(excelData);
        merges.push(
          {
            s: { r: 0, c: 0 },
            e: { r: 0, c: 2 },
          },
          {
            s: { r: 0, c: 3 },
            e: { r: 0, c: 5 },
          }
        );
        break;

      case "portfolioEfficiency":
        // Ensure there's only one header row
        ws = XLSX.utils.aoa_to_sheet([excelHeader, ...excelData]);

        // No need to merge if the header already aligns correctly
        break;

      case "prePostportfolioRecommendation":
        ws = XLSX.utils.aoa_to_sheet(excelData);
        const postRecommendationIndex = excelData.findIndex((row) =>
          row.includes("Post-Recommendation")
        );
        merges.push(
          {
            s: { r: 0, c: 0 },
            e: { r: 0, c: 5 },
          },
          {
            s: { r: postRecommendationIndex, c: 0 },
            e: { r: postRecommendationIndex, c: 5 },
          }
        );
        break;

      case "skuJourneyTable":
        // Convert the object array to a 2D array
        const formattedData = excelData.map((item) => [
          item.name,
          item.y,
          item.y1,
        ]);

        // Check if formattedData is not empty
        if (!Array.isArray(formattedData) || formattedData.length === 0) {
          console.error("No data to export for skuJourneyTable.");
          return;
        }

        // Add header if needed (uncomment if you want headers)
        // const header = ['Name', 'Value'];
        // const dataWithHeader = [header, ...formattedData];

        ws = XLSX.utils.aoa_to_sheet(formattedData);
        break;

      // New case for tail analysis charts
      case "first-tail":
        ws = XLSX.utils.aoa_to_sheet(excelData);
        // Add any specific merge logic if needed here
        break;

      case "second-tail":
        ws = XLSX.utils.aoa_to_sheet(excelData);
        // Add any specific merge logic if needed here
        break;

      case "third-tail":
        ws = XLSX.utils.aoa_to_sheet(excelData);
        // Add any specific merge logic if needed here
        break;

      case "fourth-tail":
        ws = XLSX.utils.aoa_to_sheet(excelData);
        // Add any specific merge logic if needed here
        break;

      default:
        const tableData = this.parseTableData(excelHeader, excelData);
        ws = XLSX.utils.aoa_to_sheet([tableData.header, ...tableData.data]);
        break;
    }

    if (merges.length > 0) {
      ws["!merges"] = merges;
    }
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  }

  parseTableData(excelHeader: any[], excelData: any[]) {
    const header = excelHeader.map((item) => [item.label]); // Build header array
    const data = excelData.map((item) =>
      excelHeader.map((entry) => item[entry.value])
    ); // Build data array
    return { header, data };
  }

  sendData(data: any) {
    this.dataSubject.next(data);
  }

  // Helper function convert the number
  nParseFloatNumber(val: any) {
    if (typeof val == "string") {
      return val != "" ? val : "-";
    } else if (this.isInt(val)) {
      return this.formatNumber(val);
    } else if (val && typeof val != "string" && !this.isInt(val)) {
      const floatVal: any = this.nParseFloat(val);
      return this.formatNumber(floatVal);
    } else {
      return val || "-";
    }
  }

  // Helper function for check the number integer
  isInt(num: any) {
    return Number(num) === num && num % 1 === 0;
  }

  // Helper function for format the number
  formatNumber(num: number) {
    return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
  }

  // Helper function for find the zone name
  getZoneName(zoneID: string, zoneList: any) {
    const item = zoneList.find(
      (item: any) => item.id === zoneID || item.name === zoneID
    );
    return item?.name;
  }

  removeQueryParamsFromUrl(url: string): string {
    return url.split("?")[0]; // Remove query params
  }

  // Helper function for adding the underscore instead of space between the text
  titleAddUnderscore(text: string = "") {
    return (
      text &&
      typeof text === "string" &&
      text.trim().split(" ").join("_").toLowerCase()
    );
  }

  // Helper function geting the Zone Level Image path for SKU
  getZoneImageSubUrl(experimentData: any) {
    const zoneName = this.titleAddUnderscore(experimentData.zone_name);
    const countryName = this.titleAddUnderscore(experimentData.country);
    const regionName = this.titleAddUnderscore(experimentData.region);
    const keyAccountName = this.titleAddUnderscore(experimentData.key_account);
    return `zones/${zoneName}/${countryName}/${regionName}/${keyAccountName}/`;
  }

  // Helper function geting the Zone Level Image path for SKU
  getCountryImageSubUrl(experimentData: any) {
    const zoneName = this.titleAddUnderscore(experimentData.zone_name);
    const countryName = this.titleAddUnderscore(experimentData.country);
    return `zones/${zoneName}/${countryName}/`;
  }

  getUserAccessType() {
    const userId = this.getUser()?.id;
    const userDetailsId = sessionStorage.getItem("user");
    const storedAccessType = sessionStorage.getItem("access_type");

    // If accessType already exists in session storage, return that.
    if (storedAccessType) {
      return storedAccessType;
    } else {
      // Otherwise, determine the access type and store it.
      const accessType = userId === userDetailsId ? "edit" : "read";
      sessionStorage.setItem("access_type", accessType);
      return accessType;
    }
  }

  getFileForPlanogram(
    fileType: string,
    version: string,
    menuType: string,
    expId: string,
    planId: string
  ) {
    let url;

    if (fileType === "xlsx") {
      url =
        menuType == "all"
          ? this.apiList.planogram.getExcelOfAllPlanograms(expId, version)
          : this.apiList.planogram.GetExcelOfPlanogram(expId, planId, version);
    } else {
      if (menuType == "all") {
        url =
          fileType === "pln"
            ? this.apiList.planogram.getAllPLNOfPlanogram(expId, version)
            : this.apiList.planogram.getAllPSAOfPlanogram(expId, version);
      } else {
        url =
          fileType === "pln"
            ? this.apiList.planogram.GetPLNOfPlanogram(expId, planId, version)
            : this.apiList.planogram.GetPSAOfPlanogram(expId, planId, version);
      }
    }

    // #TODO: Temporary we added this payload we have to remove when start the assortment summary(space and classic) flow
    let payload: any = {};
    let expType: any = this.checkExperimentType();
    if (
      version === "v3" &&
      fileType !== "xlsx" &&
      ["space-assortment", "space_assortment"].includes(expType)
    ) {
      payload["assortment_ref_id"] = sessionStorage.getItem("expId") || null;
    }

    return this.http.postMethod(url, payload);
  }

  getTempElement() {
    if (!this.tempElement) {
      this.tempElement = document.createElement("div");

      // Style the temp element to match the container's dimensions
      Object.assign(this.tempElement.style, {
        position: "absolute",
        visibility: "hidden",
        whiteSpace: "normal",
        lineHeight: "1",
        overflowY: "auto",
        textAlign: "center",
        wordWrap: "break-word",
        display: "none",
        top: 0,
        padding: "0",
      });
      document.body.appendChild(this.tempElement);
    }
    return this.tempElement;
  }

  getFontSize(
    text: string,
    width: number,
    height: number,
    minFontSize: number = 5,
    defaultSizeReduction: number = 2
  ): number {
    let fontSize = minFontSize; // Start with a rough estimate

    const tempEle = this.getTempElement() as HTMLElement;
    Object.assign(tempEle.style, {
      width: `${width}px`,
      height: `${height}px`,
      fontSize: `${fontSize}px`,
      display: "block",
    });
    tempEle.textContent = text + "-";

    // Decrease font size until the text fits
    while (tempEle.scrollHeight <= tempEle.clientHeight) {
      fontSize += 1;
      tempEle.style.fontSize = `${fontSize}px`;
    }

    fontSize -= 1 + defaultSizeReduction; // Decrease by 1 to get the last fitting font size, and by 2 to account for padding

    // Hide the temp element
    tempEle.style.display = "none";

    return Math.max(minFontSize, fontSize);
  }

  // Helper method to getting the experiment details from session storage
  getExpDetails(): any | null {
    const expDetailsString = sessionStorage.getItem("expDetails");
    if (expDetailsString) {
      const expDetails = JSON.parse(expDetailsString);
      return Array.isArray(expDetails) ? expDetails[0] : null;
    }
    return null;
  }

  // Helper method to getting the selected experiment details from session storage
  getSelectedExperiment(): any | null {
    const selectedExperimentString =
      sessionStorage.getItem("selectedExperiment");
    if (selectedExperimentString) {
      const expDetails = JSON.parse(selectedExperimentString);
      return expDetails || null;
    }
    return null;
  }

  // API to fetch the Plans
  updateExperimentOutdatedApiCall(expId: string) {
    const url = this.apiList.experiments.updateExperimentOutdated(expId);
    return this.http.putMethod(url);
  }

  // Helper method to set the language
  setLanguageFeatures(languageTranslation: boolean) {
    const experimentalFeatures: any = {
      languageTranslate: languageTranslation,
    };
    this.localStorageService.setInfo(
      "experimentalFeatures",
      experimentalFeatures
    );
  }

  storeAssortmentPlan() {
    let expType = sessionStorage.getItem("expType")!;
    if (["assortment"].includes(expType)) {
      sessionStorage.setItem("tempExpType", expType);
      sessionStorage.setItem(
        "linkedAssortmentId",
        sessionStorage.getItem("expId")!
      );
    }
  }

  // Get the experiment states for the linked assortment
  getExperimentStatesCall(linkedAssortmentId: string): Promise<string> {
    return this.getSidebarStatesByExpId(linkedAssortmentId)
      .pipe(map((response: any) => this.getTaskTitle(response.states)))
      .toPromise() as Promise<string>;
  }

  getTaskTitle(tasks: any) {
    const simulation = tasks.find((task: any) => task.title === "simulation");
    const clustering = tasks.find((task: any) => task.title === "clustering");

    if (simulation?.status === "COMPLETED") {
      return simulation.title;
    } else if (
      simulation &&
      (simulation.status === "IN_PROGRESS" ||
        simulation.status === "PENDING") &&
      clustering?.status === "COMPLETED"
    ) {
      return clustering.title;
    } else {
      return "eda";
    }
  }

  // Helper method get the cluster list
  getClusterProfilingList(response: any, clusterList: any) {
    const responseData = JSON.parse(response?.result);
    const clusterProfiling = JSON.parse(
      responseData.result.data.cluster_profiling
    );

    if (clusterProfiling.length > 0 && clusterList.length > 0) {
      // Convert cluster IDs to a Set of strings for fast lookup
      const clusterIdSet = new Set(
        clusterProfiling.map((item: any) => item.cluster_id.toString())
      );

      // Filter and build clusterList in one pass
      return clusterList.filter(
        (item: any) =>
          item.cluster_id === "" || clusterIdSet.has(item.cluster_id)
      );
    } else {
      return clusterList;
    }
  }

  getUserList(list: any): Observable<Map<string, string>> {
    const uniqueUserIds = Array.from(
      new Set(list.map((user: any) => user.created_by))
    );
    const payload = { users_list: uniqueUserIds };

    return this.getBulkUserDetailsService(payload).pipe(
      map((val: any) => {
        const userMap = new Map();
        if (val) {
          val.forEach((user: any) => {
            userMap.set(user.id, user.first_name + " " + user.last_name);
          });
        }
        return userMap;
      })
    );
  }

  getUserDetailsAPICall() {
    const url = `${this.apiList.user.meV3}`;
    return this.http.getMethod(url);
  }

  constructPermissionMappingsAndStoreToLocalStorage(userDetails: any): void {
    const zonesUrl = this.apiList.admin.zones;
    const permissionsUrl = this.apiList.roles.permission_mappings;

    forkJoin({
      zones: this.http.getMethod(zonesUrl),
      mappings: this.http.getMethod(permissionsUrl),
    }).subscribe(({ zones, mappings }: { zones: any[]; mappings: any }) => {
      const zoneIds: string[] = zones.map((zone: any) => zone.id);
      const zoneScopes = Object.fromEntries(
        zones.map((zone: any) => [`zones/${zone.id}`, zone.id])
      );

      const permissionMap: { [key: string]: string[] } = {};

      for (const permission in mappings) {
        const rolesForPermission = mappings[permission];
        const userZones = new Set<string>();

        for (const userRole of userDetails.roles) {
          if (!rolesForPermission.includes(userRole.id)) continue;

          for (const scope of userRole.scope) {
            if (scope === "zones/all") {
              zoneIds.forEach((id) => userZones.add(id));
            } else if (zoneScopes[scope]) {
              userZones.add(zoneScopes[scope]);
            }
          }
        }

        if (userZones.size > 0) {
          permissionMap[permission] = Array.from(userZones);
        }
      }

      userDetails.permissions = permissionMap;
      this.setUser(userDetails);
      const userStr = JSON.stringify(userDetails);
      localStorage.setItem("user-name", userStr);
      sessionStorage.setItem("user-name", userStr);

      const hasAdminAccess = [
        this.permissions.zone_users_list,
        this.permissions.zone_requests_view,
      ].some((perm) => perm in permissionMap);

      if (hasAdminAccess) {
        localStorage.setItem("admin", String(true));
      }

      userDetails["hasAdminAccess"] = hasAdminAccess;
    });
    return userDetails;
  }

  getActiveRouteSegment():
    | "studio"
    | "experiments"
    | "shared"
    | "product-library"
    | "bulk"
    | "chat"
    | "library"
    | "bulk-generate"
    | "bulk-upload"
    | "bulk-upload-image"
    | "fixture-library"
    | "add-new-fixture"
    | "store-library"
    | "store-bulk-edit"
    | "store-bulk-upload"
    | "sync-sku"
    | "sku-attribute-configuration"
    | null {
    const url = this.router.url.split("?")[0]; // Strip query params
    const segments = url.split("/").filter(Boolean); // Remove empty segments

    if (segments.includes(CatExpertRouterLink.Shared))
      return CatExpertRouterLink.Shared;
    if (segments.includes(CatExpertRouterLink.BulkUploadImage))
      return CatExpertRouterLink.BulkUploadImage;
    if (segments.includes(CatExpertRouterLink.BulkUpload))
      return CatExpertRouterLink.BulkUpload;
    if (segments.includes(CatExpertRouterLink.SyncSKU))
      return CatExpertRouterLink.SyncSKU;
    if (segments.includes(CatExpertRouterLink.SkuAttributeConfiguration))
      return CatExpertRouterLink.SkuAttributeConfiguration;
    if (segments.includes(CatExpertRouterLink.ProductLibrary))
      return CatExpertRouterLink.ProductLibrary;
    if (segments.includes(CatExpertRouterLink.Bulk))
      return CatExpertRouterLink.Bulk;
    if (
      segments.includes(CatExpertRouterLink.Experiments) ||
      segments.includes(CatExpertRouterLink.Folders)
    )
      return CatExpertRouterLink.Experiments;
    if (segments.includes(CatExpertRouterLink.NewChat))
      return CatExpertRouterLink.NewChat;
    if (segments.includes(CatExpertRouterLink.AiLibrary))
      return CatExpertRouterLink.AiLibrary;
    if (segments.includes(CatExpertRouterLink.AiBulkGenerate))
      return CatExpertRouterLink.AiBulkGenerate;
    if (segments.includes(CatExpertRouterLink.FixtureLibrary))
      return CatExpertRouterLink.FixtureLibrary;
    if (segments.includes(CatExpertRouterLink.AddNewFixture))
      return CatExpertRouterLink.AddNewFixture;
    if (segments.includes(CatExpertRouterLink.StoreLibrary))
      return CatExpertRouterLink.StoreLibrary;
    if (segments.includes(CatExpertRouterLink.StoreBulkEdit))
      return CatExpertRouterLink.StoreBulkEdit;
    if (segments.includes(CatExpertRouterLink.StoreBulkUpload))
      return CatExpertRouterLink.StoreBulkUpload;
    return CatExpertRouterLink.Studio;
  }

  hasRole(role: string) {
    return true;
    const user: any = this.getUser();
    return (
      user.roles.filter((r: { title: string }) => r.title === role).length > 0
    );
  }

  // Helper method to check 'zone admin' access for showing the product library
  hasZoneAdminRole() {
    const user: any = this.getUser();
    const roles = user?.roles?.map((r: { title: string }) => r.title) ?? [];
    return roles.includes("Zone Admin");
  }

  // Helper method to check 'AI Imagery Library Admin' access for showing the AI Imagery actions like upload, edit and delete
  hasAiImageryLibraryAdminRole() {
    const user: any = this.getUser();
    const roles = user?.roles?.map((r: { title: string }) => r.title) ?? [];
    return roles.includes("AI Imagery Library Admin");
  }

  // Helper method to check 'AI Imagery User' access for showing the AI Imagery card in the studio screen
  hasAiImageryAccessRole() {
    const user: any = this.getUser();
    const roles = user?.roles?.map((r: { title: string }) => r.title) ?? [];
    return roles.includes("AI Imagery User");
  }

  // Helper method to check 'Dashboard admin' access
  hasDashboardAdminRole() {
    const user: any = this.getUser();
    const roles = user?.roles?.map((r: { title: string }) => r.title) ?? [];
    return roles.includes("Dashboards Admin");
  }

  hasZoneAdminAndScope(): boolean {
    const user: any = this.getUser();
    if (!user?.roles?.length || !user?.zone_ids?.length) return false;

    return user.roles.some(
      (role: any) =>
        role.title === "Zone Admin" &&
        user.zone_ids.some(
          (zone: string) =>
            role.scope.includes(`zones/${zone}`) ||
            role.scope.includes("zones/all")
        )
    );
  }

  private load(key: string) {
    return JSON.parse(sessionStorage.getItem(key) || "[]");
  }

  resetWidgetsStorage() {
    sessionStorage.removeItem("savedWidget");
    sessionStorage.removeItem("pinnedWidget");
  }

  setPinnedWidgets(widgets: any[]) {
    sessionStorage.setItem("pinnedWidget", JSON.stringify(widgets));
    this.pinnedWidgetsSubject.next(widgets);
  }

  setSavedWidgets(widgets: any[]) {
    sessionStorage.setItem("savedWidget", JSON.stringify(widgets));
    this.savedWidgetsSubject.next(widgets);
  }

  getPinnedWidgetsKeys() {
    let widgets = this.load("pinnedWidget");
    return widgets.map((item: any) => item.key);
  }

  getSavedWidgetsKeys() {
    let widgets = this.load("savedWidget");
    return widgets.map((item: any) => item.key);
  }

  updatePinnedWidgetsData(
    widgets: any[],
    tab: string = "",
    isSingleWidget: boolean = false
  ) {
    this.pinnedWidgetsDataSubject.next([widgets, tab, isSingleWidget]);
  }

  clearPinnedWidgetsData() {
    this.pinnedWidgetsDataSubject.next([[], ""]);
  }
}
